import type { FlSkillId, HudSnapshot } from "../../../features/frontline";
import { t } from "../../../data/strings";
import hudPresetTable from "../../../data/tables/ui/huds/frontline.json";
import { formatInt } from "../../../view/display-format";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";
import { dockHtml, handCardsHtml, resultHtml, topHtml } from "./markup";

export type FrontlineHudHandlers = {
  onSelectHandIndex: (index: number) => void;
  onQuit: () => void;
  onRestart: () => void;
};

export type CastNoti = {
  team: 0 | 1;
  skillId: string;
};

const preset = hudPresetTable as UiHudPreset;
const NOTI_MAX = 4;
const NOTI_MS = 2500;

export function mountFrontlineHud(handlers: FrontlineHudHandlers): {
  sync: (snap: HudSnapshot, toast?: string) => void;
  pushCastNoti: (n: CastNoti) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "auto";
  top.style.position = "relative";
  top.innerHTML = topHtml();

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";
  mid.innerHTML = "";

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "auto";
  bottom.innerHTML = dockHtml();

  const modal = shell.region("modal");
  modal.hidden = true;
  modal.style.pointerEvents = "auto";
  let lastPhase: string | null = null;
  let lastHandKey = "";
  const notiTimers: ReturnType<typeof setTimeout>[] = [];

  top.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onQuit();
  });

  // Event delegation — survives hand DOM rebuilds without losing clicks.
  const handHost = bottom.querySelector("[data-hand]");
  handHost?.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement | null)?.closest?.(
      "[data-hand-idx]",
    ) as HTMLElement | null;
    if (!btn || !handHost.contains(btn)) return;
    ev.stopPropagation();
    const idx = Number(btn.dataset.handIdx);
    if (Number.isFinite(idx)) handlers.onSelectHandIndex(idx);
  });

  const bindModal = () => {
    modal.querySelector("[data-restart]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onRestart();
    });
    modal.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onQuit();
    });
  };

  const syncHandClasses = (snap: HudSnapshot, handEl: Element) => {
    handEl.querySelectorAll("[data-hand-idx]").forEach((el) => {
      const btn = el as HTMLElement;
      const idx = Number(btn.dataset.handIdx);
      const id = btn.dataset.skill as FlSkillId;
      const cost = snap.cardCosts[id] ?? 0;
      const afford = snap.mana >= cost;
      btn.classList.toggle("is-active", snap.selectedHandIndex === idx);
      btn.classList.toggle("is-poor", !afford);
      btn.classList.toggle("is-ready", afford);
    });
  };

  const pushCastNoti = (n: CastNoti) => {
    const rail = top.querySelector(
      n.team === 1 ? "[data-noti-ai]" : "[data-noti-user]",
    );
    if (!rail) return;
    const row = document.createElement("div");
    row.className = `fl-noti-row ${n.team === 1 ? "is-ai" : "is-user"}`;
    row.innerHTML = `<span class="fl-noti-who">${n.team === 1 ? t("fl.noti.ai") : t("fl.noti.user")}</span><span class="fl-noti-name">${t(`fl.skill.${n.skillId}`)}</span>`;
    rail.prepend(row);
    while (rail.children.length > NOTI_MAX) {
      rail.lastElementChild?.remove();
    }
    const timer = setTimeout(() => {
      row.classList.add("is-fade");
      setTimeout(() => row.remove(), 200);
    }, NOTI_MS);
    notiTimers.push(timer);
  };

  return {
    sync(snap, toast) {
      ui.sync();
      mid.style.pointerEvents = "none";
      mid.innerHTML = "";

      const timerEl = top.querySelector("[data-timer]");
      if (timerEl) {
        timerEl.textContent = t("fl.fmt.time", {
          n: Math.ceil(snap.timeLeft),
        });
      }
      const be = top.querySelector("[data-base-enemy]");
      const ba = top.querySelector("[data-base-ally]");
      if (be) {
        be.textContent = t("fl.fmt.baseEnemy", {
          hp: snap.baseHp[1],
          max: snap.baseMaxHp[1],
        });
      }
      if (ba) {
        ba.textContent = t("fl.fmt.baseAlly", {
          hp: snap.baseHp[0],
          max: snap.baseMaxHp[0],
        });
      }
      const msg = top.querySelector("[data-msg]");
      if (msg && toast) msg.textContent = toast;

      const elite = top.querySelector("[data-elite]");
      if (elite) {
        elite.textContent = t("fl.fmt.elite", {
          n: snap.eliteCount[0],
          lv: snap.topLevel,
        });
      }

      const fill = bottom.querySelector("[data-mana-fill]") as HTMLElement | null;
      if (fill) {
        const pct = Math.max(
          0,
          Math.min(100, (snap.mana / Math.max(1, snap.maxMana)) * 100),
        );
        fill.style.width = `${pct}%`;
      }
      const manaText = bottom.querySelector("[data-mana-text]");
      if (manaText) {
        manaText.textContent = t("fl.fmt.mana", {
          n: formatInt(snap.mana),
          max: formatInt(snap.maxMana),
        });
      }
      const deck = bottom.querySelector("[data-deck]");
      if (deck) {
        deck.textContent = t("fl.fmt.deck", {
          deck: snap.deckCount,
          discard: snap.discardCount,
          exile: snap.exileCount,
        });
      }

      // Rebuild only when hand composition or selection index changes — NOT mana.
      const handKey = `${snap.hand.join(",")}|${snap.selectedHandIndex ?? ""}`;
      const handEl = bottom.querySelector("[data-hand]");
      if (handEl && handKey !== lastHandKey) {
        lastHandKey = handKey;
        handEl.innerHTML = handCardsHtml(
          snap.hand,
          snap.selectedHandIndex,
          snap.mana,
          snap.cardCosts,
          snap.cardExile,
        );
      } else if (handEl) {
        syncHandClasses(snap, handEl);
      }

      const owned = bottom.querySelector("[data-owned]");
      if (owned) {
        owned.textContent = t("fl.fmt.owned", {
          ally: snap.ownedAlly,
          enemy: snap.ownedEnemy,
        });
      }

      if (snap.phase !== "playing") {
        if (lastPhase !== snap.phase) {
          lastPhase = snap.phase;
          modal.hidden = false;
          modal.innerHTML = resultHtml(snap.phase);
          bindModal();
        }
      } else {
        lastPhase = "playing";
        modal.hidden = true;
        modal.innerHTML = "";
      }
    },
    pushCastNoti,
    destroy() {
      for (const tmr of notiTimers) clearTimeout(tmr);
      shell.destroy();
    },
  };
}
